import { Divider } from "@msuite/ui/divider";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { type ProtocolSendFormSchema, protocolSendSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ProtocolSendForm } from "@/forms/protocol-send-form";
import { client } from "@/hono";
import { useProtocol } from "@/hooks/use-protocol";
import { SendProtocolModalRecipient } from "./send-protocol-modal-recipient";

/** Props Interface */
interface SendProtocolModalProps extends ModalProps {
	protocolId: string;
}

export function SendProtocolModal({ isOpen, onClose, protocolId }: SendProtocolModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm({
		resolver: resolver(protocolSendSchema),
		defaultValues: {
			withAttachment: true,
			sendCopyToCreator: false,
		},
	});

	/** Query */
	const { protocol } = useProtocol(protocolId);

	/** Functions */
	async function handleOnSubmit(json: ProtocolSendFormSchema) {
		const response = await client.protocols[":protocolId"].send.$post({
			param: { protocolId },
			json,
		});
		const data = await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate([protocolId]) });
		reportSendResult(data.sentCount, guardArray(data.recipients).length);
		onClose();
	}

	/**
	 * A recipient who unsubscribed from protocol mails is skipped rather than mailed, so the
	 * number of mails that went out can be lower than the number of recipients. The toast reports
	 * what was sent — the "Sent Emails" card next to it counts the same rows.
	 */
	function reportSendResult(sentCount: number, recipientCount: number) {
		if (!sentCount) {
			toast.error(t("protocols.protocol_sent_to_nobody"));
			return;
		}
		if (sentCount < recipientCount) {
			toast.success(
				t("protocols.protocol_sent_partial", {
					count: `${sentCount}`,
					total: `${recipientCount}`,
				}),
			);
			return;
		}
		toast.success(t("protocols.protocol_sent", { count: `${sentCount}` }));
	}

	/** Computed */
	const involvements = guardArray(protocol?._involvements);

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("protocols.send_protocol")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<div className="v-stack gap-4">
						<div className="v-stack gap-2">
							{!involvements.length ? (
								<p>{t("protocols.no_recipients")}</p>
							) : (
								involvements.map((involvement) => (
									<SendProtocolModalRecipient
										involvement={involvement}
										key={involvement.id}
									/>
								))
							)}
						</div>
						<Divider />
						<ProtocolSendForm />
					</div>
				</Modal.Body>
				<Modal.FooterForm submitLabel={t("general.send")} />
			</Modal>
		</FormProvider>
	);
}
